import { isFreeDeliverySentinel } from '@nandosaus/constants';
import { useRootStore } from '@nandosaus/state-management';

import { useEligibleGiftDetails } from '../use-eligible-gift-details';

/**
 * useFreeGiftOrderItem — the chosen Surprise & Delight gift as a synthetic
 * order item.
 *
 * The gift is stored on the cart store (`selectedGiftPlu`) rather than as a
 * real cart line item, so screens that show it in an order items list (cart,
 * checkout order summary, order confirmation) compose it into their list via
 * `withFreeGiftOrderItem` (state-management), which owns the gift's position
 * in the list. Carries the "Free gift" badge via `tags` and the product's
 * kilojoules when the PLU resolves in the menu (the Free Delivery sentinel
 * and not-yet-loaded PLUs simply omit the kJ line).
 *
 * Pass `removable: true` for the cart, where the row swaps the quantity
 * picker for a trash button that clears the selection. Checkout and
 * confirmation rows are static.
 *
 * Returns `null` when no gift is selected.
 */
const useFreeGiftOrderItem = ({ removable = false } = {}) => {
  const { CartStore, MenuStore } = useRootStore();
  const { selectedGiftPlu } = CartStore;
  const giftDetailsByPlu = useEligibleGiftDetails(
    CartStore.surpriseAndDelightEligibleGiftPlus ? CartStore.surpriseAndDelightEligibleGiftPlus.slice() : []
  );

  if (!selectedGiftPlu) {
    return null;
  }

  const details = giftDetailsByPlu[selectedGiftPlu] || {};
  const name = details.name || selectedGiftPlu;
  const menu = MenuStore && MenuStore.menu;
  const product = menu && typeof menu.getProductByPlu === 'function' ? menu.getProductByPlu(selectedGiftPlu) : null;

  return {
    id: `sd-free-gift-${selectedGiftPlu}`,
    isFreeGift: true,
    quantity: 1,
    name,
    product: {
      longName: name,
      resolvedImage: details.image || null,
    },
    formattedKilojoules: product ? product.formattedKilojoules : undefined,
    totals: { formattedPrice: 'FREE' },
    tags: isFreeDeliverySentinel(selectedGiftPlu) ? [] : ['Free gift'],
    ...(removable
      ? {
          removeOnly: true,
          onRemove: () => CartStore.clearSurpriseAndDelightSelection(),
        }
      : {}),
  };
};

export { useFreeGiftOrderItem };
